import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api, ksh } from "@/lib/api";
import { ArrowRight, FileText, CreditCard, Printer, ShieldCheck, Clock, Zap, Layers, Scissors, FileCheck2 } from "lucide-react";

const heroBg = "https://images.pexels.com/photos/9550363/pexels-photo-9550363.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const papers = "https://images.unsplash.com/photo-1516409590654-e8d51fc2d25c?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80";
const audience = "https://images.unsplash.com/photo-1648301033733-44554c74ec50?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80";

export default function Home() {
  const [pricing, setPricing] = useState(null);
  const [queue, setQueue] = useState(null);
  useEffect(() => { api.get("/pricing").then((r) => setPricing(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const fetchQueue = () => api.get("/public/queue").then((r) => setQueue(r.data)).catch(() => {});
    fetchQueue();
    const id = setInterval(fetchQueue, 30000);
    return () => clearInterval(id);
  }, []);

  const a4bw = pricing?.print?.A4?.bw ?? 10;
  const a4color = pricing?.print?.A4?.color ?? 50;
  const a3bw = pricing?.print?.A3?.bw ?? 20;
  const a3color = pricing?.print?.A3?.color ?? 100;
  const a5bw = pricing?.print?.A5?.bw ?? 7;
  const a5color = pricing?.print?.A5?.color ?? 35;
  const stapling = pricing?.stapling ?? 20;
  const spiralFrom = pricing?.binding?.spiral?.[0]?.price ?? 150;
  const tapeFrom = pricing?.binding?.tape?.[0]?.price ?? 100;
  const lamA4 = pricing?.lamination?.A4 ?? 100;
  const lamA3 = pricing?.lamination?.A3 ?? 200;
  const lamA5 = pricing?.lamination?.A5 ?? 70;
  return (
    <div>
      {/* HERO */}
      <section className="relative border-b border-[#E5E5E5] overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#0A0A0A] text-[10px] uppercase tracking-[0.25em] font-bold mb-6">
              <span className="w-2 h-2 bg-[#059669]" /> Nairobi · Online Print Shop
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tighter">
              UPLOAD.<br />CONFIGURE.<br />
              <span className="text-[#059669]">PRINT.</span>
            </h1>
            <p className="mt-6 text-lg text-[#525252] max-w-xl leading-relaxed">
              The fastest way to get documents printed in Nairobi. Upload your file, choose
              your specs, see the cost instantly, pay with <strong className="text-[#0A0A0A]">M-Pesa</strong>,
              and either pick up in Nairobi or have it <strong className="text-[#0A0A0A]">delivered countrywide</strong> by trusted couriers.
              No queues. No surprises.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/order"
                data-testid="home-cta-order"
                className="group inline-flex items-center justify-center gap-2 bg-[#059669] text-white px-8 py-4 text-sm uppercase tracking-[0.2em] font-bold hover:bg-[#047857] transition-colors"
              >
                Start a new order
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/track"
                data-testid="home-cta-track"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#0A0A0A] px-8 py-4 text-sm uppercase tracking-[0.2em] font-bold hover:bg-[#0A0A0A] hover:text-white transition-colors"
              >
                Track an order
              </Link>
              <Link
                to="/how-it-works"
                data-testid="home-cta-how-it-works"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 text-sm uppercase tracking-[0.2em] font-bold text-[#0A0A0A] hover:text-[#059669] transition-colors"
              >
                How it works →
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 border-t border-[#E5E5E5] pt-6">
              <div>
                <div className="font-display font-black text-3xl">{a4bw}/=</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#525252] mt-1">A4 B&amp;W / page</div>
              </div>
              <div className="border-l border-[#E5E5E5] pl-4">
                <div className="font-display font-black text-3xl">{a4color}/=</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#525252] mt-1">A4 colour / page</div>
              </div>
              <div className="border-l border-[#E5E5E5] pl-4">
                <div className="font-display font-black text-3xl">&lt;2h</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#525252] mt-1">avg turnaround</div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 bg-[#059669]/10 hidden lg:block" />
            <img
              src={heroBg}
              alt="Industrial printing press"
              className="relative w-full h-[420px] lg:h-[560px] object-cover border border-[#0A0A0A] grayscale contrast-110"
            />
            <div className="absolute bottom-4 left-4 right-4 bg-white border-2 border-[#0A0A0A] p-4 shadow-[8px_8px_0_0_#0A0A0A]">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#059669] text-white flex items-center justify-center flex-shrink-0">
                  <CreditCard className="w-6 h-6" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#525252]">Paid in seconds</div>
                  <div className="font-display font-bold text-lg">M-Pesa STK Push checkout</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LIVE QUEUE — anonymous activity counter */}
      <section className="border-b border-[#E5E5E5] bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
          <div className="flex items-center gap-2 mb-6">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#059669] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#059669]"></span>
            </span>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#059669]">Live · what&apos;s printing now</div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {[
              { label: "In the queue", val: queue?.in_queue ?? "—", hint: "Paid · waiting or printing" },
              { label: "Currently printing", val: queue?.printing ?? "—", hint: "On the machines right now" },
              { label: "Ready for pickup", val: queue?.ready_for_pickup ?? "—", hint: "Waiting for collection" },
              {
                label: "Lifetime completed",
                val: queue?.completed_lifetime != null ? queue.completed_lifetime.toLocaleString() : "—",
                hint: "Orders fulfilled all-time",
              },
            ].map((c) => (
              <div key={c.label} className="bg-[#0A0A0A] p-6">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50 mb-3">{c.label}</div>
                <div className="font-display font-black text-5xl">{c.val}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-2">{c.hint}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-[10px] uppercase tracking-[0.25em] text-white/40">
            Updated every 30 seconds · {queue?.completed_today ?? 0} order{queue?.completed_today === 1 ? "" : "s"} fulfilled today
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-8 mb-12">
            <div className="lg:col-span-4">
              <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#525252] mb-3">§ 01 · Workflow</div>
              <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
                Four steps.<br />Zero queues.
              </h2>
            </div>
            <div className="lg:col-span-8 text-lg text-[#525252] leading-relaxed">
              Built for students, professionals and SMEs in Nairobi. The whole job — from
              file upload to paid receipt — takes under a minute on your phone or laptop.
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 border border-[#E5E5E5] bg-white">
            {[
              { n: "01", icon: FileText, title: "Upload", desc: "Drop PDF, Word or images. We auto-detect page count." },
              { n: "02", icon: Layers, title: "Configure", desc: "Paper size, colour, copies, binding, stapling, lamination." },
              { n: "03", icon: CreditCard, title: "Pay", desc: "M-Pesa STK push. Approve on your phone. Instant receipt." },
              { n: "04", icon: Printer, title: "Pick up / Deliver", desc: "Collect at our Nairobi shop, or we ship anywhere in Kenya via trusted couriers." },
            ].map((s, i) => (
              <div key={s.n} className={`p-6 lg:p-8 ${i < 3 ? "border-b sm:border-b-0 sm:border-r border-[#E5E5E5]" : ""} ${i === 1 ? "border-b lg:border-b-0" : ""}`}>
                <div className="flex items-start justify-between mb-6">
                  <div className="w-12 h-12 border-2 border-[#0A0A0A] flex items-center justify-center">
                    <s.icon className="w-6 h-6" strokeWidth={2.5} />
                  </div>
                  <div className="mono text-sm text-[#525252]">{s.n}</div>
                </div>
                <h3 className="font-display font-bold text-2xl mb-2">{s.title}</h3>
                <p className="text-sm text-[#525252] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="border-b border-[#E5E5E5] bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#525252] mb-3">§ 02 · Services</div>
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-6">
              Everything a print job needs.
            </h2>
            <img src={papers} alt="Stacked paper" className="w-full h-64 object-cover border border-[#0A0A0A] grayscale" />
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
            {[
              { icon: Printer, title: "B&W Printing", price: `From KES ${a5bw}/page`, desc: `A5 ${ksh(a5bw)}, A4 ${ksh(a4bw)}, A3 ${ksh(a3bw)} per page. Crisp 600dpi on premium 80gsm.` },
              { icon: Zap, title: "Color Printing", price: `From KES ${a5color}/page`, desc: `A5 ${ksh(a5color)}, A4 ${ksh(a4color)}, A3 ${ksh(a3color)} per page. Vivid for reports & posters.` },
              { icon: Layers, title: "Spiral Binding", price: `From KES ${spiralFrom}`, desc: "Range-priced by sheet count. Pick clear, blue, red or green cover." },
              { icon: Layers, title: "Tape Binding", price: `From KES ${tapeFrom}`, desc: "Lower-cost alternative to spiral. Same sheet-range pricing, same four covers." },
              { icon: Scissors, title: "Stapling", price: `KES ${stapling}`, desc: "Per booklet. Corner or side stapling for handouts and short docs." },
              { icon: FileCheck2, title: "Lamination", price: `From KES ${lamA5}/page`, desc: `A5 ${ksh(lamA5)}, A4 ${ksh(lamA4)}, A3 ${ksh(lamA3)} per page. IDs, certificates, menus.` },
            ].map((s) => (
              <div key={s.title} className="bg-white p-6 hover:bg-[#0A0A0A] hover:text-white transition-colors group">
                <div className="flex items-start justify-between mb-4">
                  <s.icon className="w-7 h-7" strokeWidth={2} />
                  <div className="mono text-xs uppercase tracking-[0.15em] px-2 py-1 border border-current whitespace-nowrap">{s.price}</div>
                </div>
                <h3 className="font-display font-bold text-xl mb-2">{s.title}</h3>
                <p className="text-sm opacity-70 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <img src={audience} alt="Nairobi customers" className="w-full h-[420px] object-cover border border-[#0A0A0A]" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#525252] mb-3">§ 03 · Who it&apos;s for</div>
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-6">
              Built for the Nairobi rush.
            </h2>
            <div className="space-y-4">
              {[
                { t: "Students", d: "Thesis printing, lecture notes, past papers — bound and ready in two hours." },
                { t: "Professionals", d: "Reports, proposals, contracts. Pay from your phone, pick up on your way to the meeting." },
                { t: "Small Businesses", d: "Flyers, menus, business cards, training manuals — at predictable, transparent prices." },
              ].map((x) => (
                <div key={x.t} className="border-l-2 border-[#059669] pl-4 py-2">
                  <div className="font-display font-bold text-xl">{x.t}</div>
                  <div className="text-sm text-[#525252] mt-1">{x.d}</div>
                </div>
              ))}
            </div>
            <Link to="/order" className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] font-bold text-[#059669] hover:gap-3 transition-all">
              Start your order <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-20 text-center">
          <div className="inline-flex items-center gap-2 mb-4">
            <Clock className="w-4 h-4 text-[#059669]" />
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#059669]">Open Mon–Sat · 8AM–8PM</span>
          </div>
          <h2 className="font-display text-4xl sm:text-6xl font-black tracking-tighter mb-6">
            Ready to print something?
          </h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto mb-8">
            Upload your file now and see your total cost in seconds. No account required for guest checkout.
          </p>
          <Link
            to="/order"
            className="inline-flex items-center gap-2 bg-[#059669] text-white px-10 py-5 text-sm uppercase tracking-[0.2em] font-bold hover:bg-[#047857] transition-colors"
          >
            Upload & Print Now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}

